import React, { useEffect, useMemo, useState } from 'react';
import { Modal, ESTADO_HORAS, estadoHoras, nf, fechaCorta } from '@/pages/dashboard/components/SalaControl';
import { obtenerSemanasCentro } from '../services/horasAutorizadas.services';

// Barra de la semana (como la del detalle del informe): gris = planeado a la fecha, azul =
// ejecutado (ámbar si la semana no se planificó), rojo = exceso sobre el plan y una marca con
// las horas autorizadas.
const BarraSemana = ({ s, max }) => {
  const pct = (h) => `${Math.max(0, (h / max) * 100)}%`;
  const plan = s.planificada ? s.planeadasACorte : 0;
  const exceso = Math.max(0, s.ejecutadas - plan);
  return (
    <div className="bar-track" style={{ width: 140, position: 'relative', overflow: 'visible' }}>
      <div className="bar-fill" style={{ position: 'absolute', inset: 0, width: pct(plan), background: 'var(--rule-strong)' }} />
      {s.estado !== 'futura' && (
        <div
          className="bar-fill"
          style={{
            position: 'absolute',
            top: 2,
            bottom: 2,
            left: 0,
            width: pct(Math.min(s.ejecutadas, plan || s.ejecutadas)),
            background: s.planificada ? 'var(--s1)' : 'var(--warning)',
          }}
        />
      )}
      {s.estado !== 'futura' && s.planificada && exceso > 0 && (
        <div
          style={{
            position: 'absolute',
            top: 2,
            bottom: 2,
            left: pct(plan),
            width: pct(exceso),
            background: 'var(--critical)',
            borderRadius: '0 4px 4px 0',
          }}
        />
      )}
      {s.autorizadas != null && (
        <div
          title={`Autorizadas: ${nf(s.autorizadas, 1)} h`}
          style={{ position: 'absolute', top: -3, bottom: -3, width: 2, left: pct(s.autorizadas), background: 'var(--ink)', borderRadius: 1 }}
        />
      )}
    </div>
  );
};

// Color del índice: por encima del promedio en verde, un poco por debajo en ámbar, muy por debajo en rojo
const colorIndice = (i) => (i == null ? 'var(--rule-strong)' : i >= 100 ? 'var(--good)' : i >= 90 ? 'var(--warning)' : 'var(--critical)');
const tintaIndice = (i) => (i == null ? 'var(--ink-3)' : i >= 100 ? 'var(--good-ink)' : i >= 90 ? 'var(--warn-ink)' : 'var(--critical-ink)');

// Producción de cada semana frente a las horas ejecutadas y el índice que define la mejor semana
const Productividad = ({ data }) => {
  const { semanas, productividad: p } = data;
  const maxIndice = Math.max(120, ...semanas.map((s) => s.indice || 0));
  const total = (k) => semanas.reduce((t, s) => t + (s[k] || 0), 0);
  const kg = total('kilosLavados');
  const prendas = total('prendas');
  const horas = total('ejecutadas');
  return (
    <>
      <div className="card-head" style={{ padding: '18px 0 8px' }}>
        <h3>Producción y productividad</h3>
        <span className="spacer" />
        <span className="eyebrow">
          promedio semanas cerradas: {p.promedioKgPorHora == null ? '—' : nf(p.promedioKgPorHora, 1)} kg/h ·{' '}
          {p.promedioPrendasPorHora == null ? '—' : nf(p.promedioPrendasPorHora, 1)} prendas/h
        </span>
      </div>
      <div className="tbl-scroll">
        <table className="data">
          <thead>
            <tr>
              <th>Semana</th>
              <th>Kilos lavados</th>
              <th>Ciclos</th>
              <th>Prendas alistadas</th>
              <th>Horas ejecutadas</th>
              <th>Kg / hora</th>
              <th>Prendas / hora</th>
              <th>Índice</th>
            </tr>
          </thead>
          <tbody>
            {semanas.map((s) => (
              <tr
                key={s.weekStart}
                style={
                  s.mejor
                    ? { background: 'color-mix(in srgb, var(--good) 9%, var(--surface))' }
                    : s.estado === 'futura'
                      ? { opacity: 0.55 }
                      : undefined
                }
              >
                <td style={{ whiteSpace: 'nowrap' }}>
                  <div style={{ fontWeight: 500, fontSize: 13 }}>
                    {fechaCorta(s.weekStart)} – {fechaCorta(s.weekEnd)}
                    {s.mejor && <span style={{ color: 'var(--good-ink)' }}> ★</span>}
                  </div>
                  {s.mejor && <div className="eyebrow" style={{ color: 'var(--good-ink)' }}>mejor semana</div>}
                  {s.estado === 'en_curso' && <div className="eyebrow">en curso · parcial</div>}
                </td>
                <td>{s.estado === 'futura' ? '' : nf(s.kilosLavados, 0)}</td>
                <td>{s.estado === 'futura' ? '' : nf(s.ciclos, 0)}</td>
                <td>{s.estado === 'futura' ? '' : nf(s.prendas, 0)}</td>
                <td>{s.estado === 'futura' ? '' : nf(s.ejecutadas, 1)}</td>
                <td>{s.kgPorHora == null ? '—' : nf(s.kgPorHora, 1)}</td>
                <td>{s.prendasPorHora == null ? '—' : nf(s.prendasPorHora, 1)}</td>
                <td>
                  <div className="bar-cell">
                    <span style={{ fontWeight: 600, color: tintaIndice(s.indice), minWidth: 44, textAlign: 'right' }}>
                      {s.indice == null ? '—' : nf(s.indice, 1)}
                    </span>
                    <div className="bar-track" style={{ width: 90, position: 'relative', overflow: 'visible' }}>
                      <div className="bar-fill" style={{ width: `${Math.min(100, ((s.indice || 0) / maxIndice) * 100)}%`, background: colorIndice(s.indice) }} />
                      {/* Marca del promedio (100) */}
                      <div style={{ position: 'absolute', top: -3, bottom: -3, width: 1, left: `${(100 / maxIndice) * 100}%`, background: 'var(--ink-3)' }} />
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td style={{ fontWeight: 600 }}>Total 4 semanas</td>
              <td>{nf(kg, 0)}</td>
              <td>{nf(total('ciclos'), 0)}</td>
              <td>{nf(prendas, 0)}</td>
              <td>{nf(horas, 1)}</td>
              <td>{horas ? nf(kg / horas, 1) : '—'}</td>
              <td>{horas ? nf(prendas / horas, 1) : '—'}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="card-sub" style={{ marginTop: 10 }}>
        Kilos lavados = ciclos de lavado no abortados; prendas alistadas = alistamientos ya hechos (sin pendientes ni eliminados);
        horas ejecutadas sin conductores. Índice = promedio de (kg/hora ÷ promedio de las semanas cerradas) y (prendas/hora ÷
        promedio) × 100: 100 es una semana normal, más de 100 es más productiva. La mejor semana es la cerrada con mayor índice;
        la semana en curso se muestra pero no compite porque aún no termina.
      </p>
    </>
  );
};

const Kpi = ({ titulo, valor, unidad, sub, color }) => (
  <div className="kpi">
    <span className="eyebrow">{titulo}</span>
    <div className="kpi-val num" style={color ? { color } : undefined}>
      {valor}
      {unidad && <span className="kpi-unit">{unidad}</span>}
    </div>
    <div className="kpi-foot">
      <span className="kpi-sub">{sub}</span>
    </div>
  </div>
);

// Últimas 4 semanas de un centro: planeadas vs. ejecutadas y horas autorizadas, con el estilo
// del informe "Horas del mes: planeadas vs. ejecutadas" del dashboard.
const SemanasCentroDialog = ({ centro, hasta, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!centro) return undefined;
    let vigente = true;
    setData(null);
    setError(null);
    obtenerSemanasCentro(centro.id, hasta, 4)
      .then((d) => vigente && setData(d))
      .catch((e) => vigente && setError(e.message));
    return () => { vigente = false; };
  }, [centro, hasta]);

  const r = useMemo(() => {
    if (!data) return null;
    const semanas = data.semanas;
    const planificadas = semanas.filter((s) => s.planificada);
    const cerradas = semanas.filter((s) => s.estado === 'cerrada');
    const cerradasPlan = cerradas.filter((s) => s.planificada);
    const suma = (lista, k) => lista.reduce((t, s) => t + (s[k] || 0), 0);
    const planeadas = suma(planificadas, 'planeadasACorte');
    const ejecPlan = suma(planificadas, 'ejecutadas');
    return {
      max: Math.max(1, ...semanas.flatMap((s) => [s.planeadasACorte, s.ejecutadas, s.autorizadas || 0])),
      planeadas,
      ejecutadas: suma(semanas, 'ejecutadas'),
      sinPlan: suma(semanas.filter((s) => !s.planificada), 'ejecutadas'),
      extras: suma(semanas, 'extras'),
      nocturnas: suma(semanas, 'nocturnas'),
      cumplimiento: planeadas ? (ejecPlan / planeadas) * 100 : null,
      promEjecutadas: cerradas.length ? suma(cerradas, 'ejecutadas') / cerradas.length : null,
      promPlaneadas: cerradasPlan.length ? suma(cerradasPlan, 'planeadas') / cerradasPlan.length : null,
      cerradas: cerradas.length,
      ultimaAutorizada: [...semanas].reverse().find((s) => s.autorizadas != null)?.autorizadas ?? null,
    };
  }, [data]);

  if (!centro) return null;
  const eGlobal = ESTADO_HORAS[estadoHoras(r?.cumplimiento ?? null)];

  return (
    <Modal
      titulo={`Últimas 4 semanas · ${centro.nombre}`}
      subtitulo={
        r
          ? `${nf(r.ejecutadas, 1)} h ejecutadas de ${nf(r.planeadas, 1)} h planeadas${r.sinPlan ? ` · ${nf(r.sinPlan, 1)} h sin plan` : ''} · sin conductores`
          : 'Cargando…'
      }
      onCerrar={onClose}
      ancho={1100}
      acciones={
        r && (
          <span className="pill" style={{ '--sev': eGlobal.sev, '--sev-ink': eGlobal.ink }}>
            <i className="dot" />
            {r.cumplimiento == null ? eGlobal.texto : `${nf(r.cumplimiento, 1)} %`}
          </span>
        )
      }
    >
      {error && <div className="empty">{error}</div>}
      {!error && !data && <div className="empty">Cargando semanas…</div>}

      {data && r && (
        <>
          <div className="kpis" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
            {(() => {
              const m = data.semanas.find((s) => s.mejor);
              return (
                <Kpi
                  titulo="Mejor semana"
                  valor={m ? `${fechaCorta(m.weekStart)}` : '—'}
                  color={m ? 'var(--good-ink)' : undefined}
                  sub={
                    m
                      ? `índice ${nf(m.indice, 1)} · ${nf(m.kgPorHora, 1)} kg/h · ${nf(m.prendasPorHora, 1)} prendas/h`
                      : 'sin semanas cerradas con producción'
                  }
                />
              );
            })()}
            <Kpi
              titulo="Promedio ejecutado"
              valor={r.promEjecutadas == null ? '—' : nf(r.promEjecutadas, 1)}
              unidad={r.promEjecutadas == null ? null : 'h'}
              sub={`por semana cerrada (${r.cerradas})`}
            />
            <Kpi
              titulo="Promedio planeado"
              valor={r.promPlaneadas == null ? '—' : nf(r.promPlaneadas, 1)}
              unidad={r.promPlaneadas == null ? null : 'h'}
              sub={r.ultimaAutorizada != null ? `autorizadas: ${nf(r.ultimaAutorizada, 1)} h por semana` : 'sin horas autorizadas configuradas'}
            />
            <Kpi
              titulo="Cumplimiento"
              valor={r.cumplimiento == null ? '—' : nf(r.cumplimiento, 1)}
              unidad={r.cumplimiento == null ? null : '%'}
              color={eGlobal.ink}
              sub="ejecutado / planeado a la fecha"
            />
            <Kpi
              titulo="Extras · nocturnas"
              valor={nf(r.extras, 1)}
              unidad="h"
              color={r.extras > 0 ? 'var(--warn-ink)' : undefined}
              sub={`${nf(r.nocturnas, 1)} h nocturnas · exceso sobre ${data.limiteSemanal} h/semana`}
            />
          </div>

          <div className="alert-tabs" style={{ padding: '14px 0 10px' }}>
            <span className="legend-inline" style={{ marginLeft: 'auto' }}>
              <span><i className="dot" style={{ background: 'var(--rule-strong)' }} />planeadas</span>
              <span><i className="dot" style={{ background: 'var(--s1)' }} />ejecutadas</span>
              <span><i className="dot" style={{ background: 'var(--critical)' }} />sobre el plan</span>
              <span><i className="dot" style={{ background: 'var(--warning)' }} />sin planificar</span>
              <span><i className="dot" style={{ background: 'var(--ink)', borderRadius: 1, width: 2 }} />autorizadas</span>
            </span>
          </div>

          <div className="tbl-scroll">
            <table className="data">
              <thead>
                <tr>
                  <th>Semana</th>
                  <th>Planeadas vs. ejecutadas</th>
                  <th>Autorizadas</th>
                  <th>Planeadas</th>
                  <th>Ejecutadas</th>
                  <th>Diferencia</th>
                  <th>Cumplimiento</th>
                  <th>Personas</th>
                  <th>Extras</th>
                  <th>Nocturnas</th>
                </tr>
              </thead>
              <tbody>
                {data.semanas.map((s) => {
                  const e = ESTADO_HORAS[s.estadoCumplimiento] || ESTADO_HORAS.sin_datos;
                  const sobreAutorizado = s.autorizadas != null && s.planeadas > s.autorizadas;
                  return (
                    <tr key={s.weekStart} style={s.estado === 'futura' ? { opacity: 0.55 } : undefined}>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <div style={{ fontWeight: 500, fontSize: 13 }}>
                          {fechaCorta(s.weekStart)} – {fechaCorta(s.weekEnd)}
                        </div>
                        {(s.estado !== 'cerrada' || !s.planificada) && (
                          <div className="eyebrow">
                            {[
                              s.estado === 'en_curso' ? `en curso · hasta ${data.horaCorte}` : null,
                              s.estado === 'futura' ? 'futura' : null,
                              !s.planificada && s.estado !== 'futura' ? 'sin planificar' : null,
                            ].filter(Boolean).join(' · ')}
                          </div>
                        )}
                      </td>
                      <td><BarraSemana s={s} max={r.max} /></td>
                      <td title={s.motivoLimite || undefined}>
                        {s.autorizadas == null ? '—' : nf(s.autorizadas, 1)}
                        {s.fuenteLimite === 'semana' && <span className="eyebrow"> · ajuste</span>}
                      </td>
                      <td
                        style={sobreAutorizado ? { color: 'var(--critical-ink)', fontWeight: 600 } : undefined}
                        title={sobreAutorizado ? 'Se planificó más de lo autorizado' : undefined}
                      >
                        {s.planificada ? nf(s.planeadas, 1) : '—'}
                        {s.estado === 'en_curso' && s.planificada && (
                          <div className="eyebrow">{nf(s.planeadasACorte, 1)} a la fecha</div>
                        )}
                      </td>
                      <td>
                        {s.estado === 'futura' ? '' : nf(s.ejecutadas, 1)}
                        {s.conductores.ejecutadas > 0 && <div className="eyebrow">+{nf(s.conductores.ejecutadas, 1)} cond.</div>}
                      </td>
                      <td style={s.planificada && s.diferencia > 0 ? { color: 'var(--critical-ink)', fontWeight: 600 } : undefined}>
                        {s.planificada && s.estado !== 'futura' ? `${s.diferencia > 0 ? '+' : ''}${nf(s.diferencia, 1)}` : '—'}
                      </td>
                      <td>
                        <span className="pill" style={{ '--sev': e.sev, '--sev-ink': e.ink }}>
                          <i className="dot" />
                          {s.cumplimiento == null ? e.texto : `${nf(s.cumplimiento, 1)} %`}
                        </span>
                      </td>
                      <td>{s.personasMarcaron} / {s.personasPlaneadas}</td>
                      <td style={s.extras > 0 ? { color: 'var(--warn-ink)' } : undefined}>{s.extras ? nf(s.extras, 1) : '—'}</td>
                      <td>{s.nocturnas ? nf(s.nocturnas, 1) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td style={{ fontWeight: 600 }}>Total 4 semanas</td>
                  <td />
                  <td />
                  <td>{nf(r.planeadas, 1)}</td>
                  <td>{nf(r.ejecutadas, 1)}</td>
                  <td />
                  <td style={{ color: eGlobal.ink, fontWeight: 600 }}>{r.cumplimiento == null ? '—' : `${nf(r.cumplimiento, 1)} %`}</td>
                  <td />
                  <td>{r.extras ? nf(r.extras, 1) : '—'}</td>
                  <td>{r.nocturnas ? nf(r.nocturnas, 1) : '—'}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <p className="card-sub" style={{ marginTop: 10 }}>
            Mismas reglas del informe «Horas del mes»: ejecutadas = horas autorizadas o, si aún no se autorizan, las marcadas; la
            semana en curso compara lo planeado hasta esta hora. Personas = empleados que marcaron / planificados. Los conductores
            no se incluyen. Semáforo: 95–100 % en meta, 85–95 % alerta, &lt;85 % crítico; por encima del 100 % es sobrecosto de
            personal (en rojo si supera el 110 %).
          </p>

          <Productividad data={data} />
        </>
      )}
    </Modal>
  );
};

export default SemanasCentroDialog;
